// Employees page — task 104c D3.
//
// Route stays `/settings/team` for muscle memory, but the page is now the
// Employees surface (104 § Q7). File name kept for the router mapping; the
// content and title are what changed. The sidebar label is "Employees"
// because "Team" misdescribed this page — it used to show Memberships,
// which is now a separate surface (/settings/access, shipping in 104d).

import {
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/composites/PageHeader';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { DataTable } from '@/components/composites/DataTable';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { InviteMemberModal } from '@/components/composites/InviteMemberModal';
import { Button } from '@/components/primitives/Button';
import { Badge } from '@/components/primitives/Badge';
import { ActionLink } from '@/components/primitives/ActionLink';
import { TextInput } from '@/components/primitives/TextInput';
import { Select } from '@/components/primitives/Select';
import { Toggle } from '@/components/primitives/Toggle';
import { useAppContext } from '@/context/AppContext';
import { formatDayFull } from '@/lib/dates';
import {
  useEmployeesAdmin,
  useCreateEmployee,
  useEditEmployee,
  useDeactivateEmployee,
  useReactivateEmployee,
  useLinkUser,
  useUnlinkUser,
  type EmployeeAdmin,
} from '@/hooks/useEmployeesAdmin';
import { useOrgAccess } from '@/hooks/useOrgAccess';
import type { StatCardModel } from '@/types/stats';
import styles from './SettingsTeamPage.module.css';

type Col = 'name' | 'email' | 'businessUnit' | 'linked' | 'source' | 'actions';

type ModalState =
  | { kind: 'closed' }
  | { kind: 'add' }
  | { kind: 'edit'; employee: EmployeeAdmin }
  | { kind: 'link'; employee: EmployeeAdmin }
  | { kind: 'invite'; employee: EmployeeAdmin }
  | { kind: 'deactivate'; employee: EmployeeAdmin };

export function SettingsTeamPage() {
  const { currentUser } = useAppContext();
  const isAdmin = currentUser.role === 'admin';
  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [search, setSearch] = useState('');
  const [buFilter, setBuFilter] = useState<string | ''>('');
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });

  const { employees, businessUnits, isLoading } = useEmployeesAdmin({
    includeDeactivated: isAdmin ? includeDeactivated : false,
    search: search.trim() === '' ? undefined : search.trim(),
  });

  // 104e D1 — the per-row "Invite as Kanjoy user" action disables when a
  // pending invite already exists for `(orgId, email)`. The page already
  // has `useOrgAccess` wired for the sibling SettingsAccessPage, and the
  // cost of another query here is zero on the mockup path. In live mode
  // the query runs in parallel with the employees fetch and its cache key
  // matches the Access page's, so both pages share the result.
  const { pendingInvites } = useOrgAccess();
  const pendingEmailSet = useMemo(() => {
    const set = new Set<string>();
    for (const inv of pendingInvites) set.add(inv.email.toLowerCase());
    return set;
  }, [pendingInvites]);

  const filtered = useMemo(() => {
    if (!buFilter) return employees;
    return employees.filter((e) => e.businessUnitId === buFilter);
  }, [employees, buFilter]);

  const buName = (id: string | null): string | null => {
    if (!id) return null;
    const bu = businessUnits.find((b) => b.id === id);
    return bu ? bu.name : null;
  };

  const totalActive = employees.filter((e) => e.deactivatedAt === null).length;
  const totalLinked = employees.filter((e) => e.userId !== null && e.deactivatedAt === null).length;
  const totalUnlinked = employees.filter(
    (e) => e.userId === null && e.deactivatedAt === null,
  ).length;
  const totalDeactivated = employees.filter((e) => e.deactivatedAt !== null).length;

  const stats: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Total employees', value: String(totalActive) },
    { label: 'Linked to Kanjoy users', value: String(totalLinked) },
    { label: 'No Kanjoy link', value: String(totalUnlinked) },
    { label: 'Deactivated', value: String(totalDeactivated) },
  ];

  const buOptions = businessUnits.map((b) => ({ value: b.id, label: b.name }));

  const columns: Array<{
    key: Col;
    header: string;
    align?: 'left' | 'right';
    render: (row: EmployeeAdmin) => ReactNode;
  }> = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <span className={row.deactivatedAt ? styles.mutedRow : styles.name}>
          <Link to={`/employees/${row.id}`} className={styles.nameLink}>
            {row.name}
          </Link>
          {row.deactivatedAt && (
            <>
              {' '}
              <Badge variant="disabled">Deactivated</Badge>
            </>
          )}
        </span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (row) =>
        row.email ? (
          <span className={styles.email}>{row.email}</span>
        ) : (
          <Badge variant="general">No email on record</Badge>
        ),
    },
    {
      key: 'businessUnit',
      header: 'Business Unit',
      render: (row) => {
        const name = buName(row.businessUnitId);
        return name ?? <span className={styles.mutedRow}>{'—'}</span>;
      },
    },
    {
      key: 'linked',
      header: 'Linked to Kanjoy user?',
      render: (row) =>
        row.userId ? (
          <Badge variant="enabled">Linked</Badge>
        ) : (
          <Badge variant="general">Not linked</Badge>
        ),
    },
    {
      key: 'source',
      header: 'Source',
      render: (row) =>
        row.collectorId ? (
          <Badge variant="billable">Insightful</Badge>
        ) : (
          <Badge variant="general">Reporting only</Badge>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => {
        if (!isAdmin) return null;
        // 104e D1 — per-row invite gating. The action is disabled when
        // the employee can't meaningfully be invited; the tooltip states
        // the reason so admins don't guess.
        const emailLower = row.email?.trim().toLowerCase() ?? '';
        const hasPendingInvite = emailLower !== '' && pendingEmailSet.has(emailLower);
        let inviteDisabledReason: string | null = null;
        if (row.deactivatedAt !== null) {
          inviteDisabledReason = 'Deactivated employees cannot be invited';
        } else if (!row.email) {
          inviteDisabledReason = 'No email on record for this employee';
        } else if (row.userId !== null) {
          inviteDisabledReason = 'Already linked to a Kanjoy user';
        } else if (hasPendingInvite) {
          inviteDisabledReason = 'An invite is already pending for this email';
        }
        const inviteDisabled = inviteDisabledReason !== null;
        return (
          <span className={styles.actions}>
            <ActionLink onClick={() => setModal({ kind: 'edit', employee: row })}>Edit</ActionLink>
            <ActionLink
              disabled={inviteDisabled}
              title={inviteDisabledReason ?? undefined}
              onClick={() => {
                if (inviteDisabled) return;
                setModal({ kind: 'invite', employee: row });
              }}
            >
              Invite as Kanjoy user
            </ActionLink>
            {row.userId === null && row.deactivatedAt === null && (
              <ActionLink onClick={() => setModal({ kind: 'link', employee: row })}>
                Link user
              </ActionLink>
            )}
            {row.userId !== null && row.deactivatedAt === null && (
              <UnlinkAction employeeId={row.id} />
            )}
            {row.deactivatedAt === null ? (
              <ActionLink danger onClick={() => setModal({ kind: 'deactivate', employee: row })}>
                Deactivate
              </ActionLink>
            ) : (
              <ReactivateAction employeeId={row.id} />
            )}
          </span>
        );
      },
    },
  ];

  const visibleColumns = isAdmin ? columns : columns.filter((c) => c.key !== 'actions');

  return (
    <>
      <PageHeader
        title="Employees"
        meta={
          <span className={styles.summaryText}>
            {totalActive} employees {'·'} {totalLinked} linked to Kanjoy users
          </span>
        }
        actions={
          isAdmin ? (
            <Button variant="primary" onClick={() => setModal({ kind: 'add' })}>
              Add Employee
            </Button>
          ) : undefined
        }
      />
      <div className={styles.statsRow}>
        <StatCardGrid cards={stats} />
      </div>
      <div className={styles.filtersRow} role="group" aria-label="Employee filters">
        <div className={styles.searchSlot}>
          <TextInput
            label="Search"
            value={search}
            placeholder="Name, email, or login"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.filterSlot}>
          <Select
            label="Business Unit"
            value={buFilter}
            options={[{ value: '', label: 'All business units' }, ...buOptions]}
            onChange={(v) => setBuFilter(v)}
          />
        </div>
        {isAdmin && (
          <div className={styles.toggleSlot}>
            <Toggle
              label="Show deactivated"
              checked={includeDeactivated}
              onChange={setIncludeDeactivated}
            />
          </div>
        )}
      </div>
      <DataTable<EmployeeAdmin, Col>
        caption="Employees"
        columns={visibleColumns}
        rows={filtered as EmployeeAdmin[]}
        getRowKey={(row) => row.id}
        emptyState={isLoading ? 'Loading employees…' : 'No employees match the current filters.'}
      />
      <AddEmployeeModal
        open={modal.kind === 'add'}
        onClose={() => setModal({ kind: 'closed' })}
        businessUnits={buOptions}
      />
      <EditEmployeeModal
        state={modal}
        onClose={() => setModal({ kind: 'closed' })}
        businessUnits={buOptions}
      />
      <LinkUserModal state={modal} onClose={() => setModal({ kind: 'closed' })} />
      <DeactivateEmployeeModal state={modal} onClose={() => setModal({ kind: 'closed' })} />
      <InviteMemberModal
        open={modal.kind === 'invite'}
        onClose={() => setModal({ kind: 'closed' })}
        canInviteOwner={isAdmin}
        lockedEmployee={
          modal.kind === 'invite' && modal.employee.email
            ? {
                id: modal.employee.id,
                name: modal.employee.name,
                email: modal.employee.email,
              }
            : undefined
        }
      />
    </>
  );
}

// ── Small action helpers (inline mutations) ──────────────────────────────

function UnlinkAction({ employeeId }: { employeeId: string }) {
  const unlink = useUnlinkUser();
  return (
    <ActionLink
      onClick={() => {
        void unlink.mutateAsync(employeeId);
      }}
      disabled={unlink.isPending}
    >
      Unlink user
    </ActionLink>
  );
}

function ReactivateAction({ employeeId }: { employeeId: string }) {
  const reactivate = useReactivateEmployee();
  return (
    <ActionLink
      onClick={() => {
        void reactivate.mutateAsync(employeeId);
      }}
      disabled={reactivate.isPending}
    >
      Reactivate
    </ActionLink>
  );
}

// ── Modals ───────────────────────────────────────────────────────────────

interface BuOption {
  value: string;
  label: string;
}

function AddEmployeeModal({
  open,
  onClose,
  businessUnits,
}: {
  open: boolean;
  onClose: () => void;
  businessUnits: BuOption[];
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userLogin, setUserLogin] = useState('');
  const [businessUnitId, setBusinessUnitId] = useState<string | ''>('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateEmployee();

  const reset = () => {
    setName('');
    setEmail('');
    setUserLogin('');
    setBusinessUnitId('');
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      await create.mutateAsync({
        name: name.trim(),
        email: email.trim() === '' ? undefined : email.trim(),
        userLogin: userLogin.trim() === '' ? undefined : userLogin.trim(),
        businessUnitId: businessUnitId === '' ? undefined : businessUnitId,
      });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create employee');
    }
  };

  return (
    <SlidePanel
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Add Employee"
      subtitle="Create a reporting-only Employee. No collector link."
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <TextInput label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <TextInput
          label="Email (optional)"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextInput
          label="User login (optional)"
          value={userLogin}
          onChange={(e) => setUserLogin(e.target.value)}
        />
        <Select
          label="Business Unit (optional)"
          value={businessUnitId}
          options={[{ value: '', label: 'None' }, ...businessUnits]}
          onChange={(v) => setBusinessUnitId(v)}
        />
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <div className={styles.formActions}>
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Add employee'}
          </Button>
        </div>
      </form>
    </SlidePanel>
  );
}

function EditEmployeeModal({
  state,
  onClose,
  businessUnits,
}: {
  state: ModalState;
  onClose: () => void;
  businessUnits: BuOption[];
}) {
  const open = state.kind === 'edit';
  const employee = state.kind === 'edit' ? state.employee : null;
  return (
    <SlidePanel open={open} onClose={onClose} title="Edit Employee">
      {employee ? (
        <EditEmployeeForm
          key={employee.id}
          employee={employee}
          businessUnits={businessUnits}
          onDone={onClose}
        />
      ) : (
        <p className={styles.placeholder}>No employee selected.</p>
      )}
    </SlidePanel>
  );
}

function EditEmployeeForm({
  employee,
  businessUnits,
  onDone,
}: {
  employee: EmployeeAdmin;
  businessUnits: BuOption[];
  onDone: () => void;
}) {
  const [name, setName] = useState(employee.name);
  const [email, setEmail] = useState(employee.email ?? '');
  const [userLogin, setUserLogin] = useState(employee.userLogin ?? '');
  const [businessUnitId, setBusinessUnitId] = useState<string | ''>(employee.businessUnitId ?? '');
  const [error, setError] = useState<string | null>(null);
  const edit = useEditEmployee();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      await edit.mutateAsync({
        id: employee.id,
        name: name.trim(),
        email: email.trim() === '' ? null : email.trim(),
        userLogin: userLogin.trim() === '' ? null : userLogin.trim(),
        businessUnitId: businessUnitId === '' ? null : businessUnitId,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update employee');
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      {employee.sourceNativeId && (
        <div className={styles.readonlyChip} aria-label="Collector-sourced identifier">
          <span className={styles.chipLabel}>Insightful ID</span>
          <span className={styles.chipValue}>{employee.sourceNativeId}</span>
        </div>
      )}
      <TextInput label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
      <TextInput
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <TextInput
        label="User login"
        value={userLogin}
        onChange={(e) => setUserLogin(e.target.value)}
      />
      <Select
        label="Business Unit"
        value={businessUnitId}
        options={[{ value: '', label: 'None' }, ...businessUnits]}
        onChange={(v) => setBusinessUnitId(v)}
      />
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.formActions}>
        <Button variant="secondary" type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={edit.isPending}>
          {edit.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

function LinkUserModal({ state, onClose }: { state: ModalState; onClose: () => void }) {
  const open = state.kind === 'link';
  const employee = state.kind === 'link' ? state.employee : null;
  // In the interest of keeping 104c self-contained, we surface the
  // target user-id input as free-form — the "dropdown of unlinked active
  // Memberships" lands in 104d when the Access page (and its user list
  // hook) ships. A free-form input is sufficient to validate the
  // round-trip for the RBAC / tenant-isolation surface 104c covers.
  const [userId, setUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <SlidePanel open={open} onClose={onClose} title="Link to Kanjoy user">
      {employee ? (
        <LinkUserForm
          key={employee.id}
          employeeId={employee.id}
          userId={userId}
          setUserId={setUserId}
          error={error}
          setError={setError}
          onDone={() => {
            setUserId('');
            setError(null);
            onClose();
          }}
        />
      ) : (
        <p className={styles.placeholder}>No employee selected.</p>
      )}
    </SlidePanel>
  );
}

function LinkUserForm({
  employeeId,
  userId,
  setUserId,
  error,
  setError,
  onDone,
}: {
  employeeId: string;
  userId: string;
  setUserId: Dispatch<SetStateAction<string>>;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
  onDone: () => void;
}) {
  const link = useLinkUser();
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!userId.trim()) {
      setError('User id is required.');
      return;
    }
    try {
      await link.mutateAsync({ employeeId, userId: userId.trim() });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link user');
    }
  };
  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <p className={styles.description}>
        Pick the Kanjoy user who should be linked to this employee. The user must already be an
        active member of this organization.
      </p>
      <TextInput
        label="Kanjoy user id"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
        required
      />
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.formActions}>
        <Button variant="secondary" type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={link.isPending}>
          {link.isPending ? 'Linking…' : 'Link user'}
        </Button>
      </div>
    </form>
  );
}

function DeactivateEmployeeModal({ state, onClose }: { state: ModalState; onClose: () => void }) {
  const open = state.kind === 'deactivate';
  const employee = state.kind === 'deactivate' ? state.employee : null;
  const deactivate = useDeactivateEmployee();
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!employee) return;
    setError(null);
    try {
      await deactivate.mutateAsync(employee.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate');
    }
  };

  return (
    <SlidePanel open={open} onClose={onClose} title="Deactivate employee">
      {employee ? (
        <div className={styles.form}>
          <p className={styles.description}>
            Deactivate <strong>{employee.name}</strong>?
          </p>
          <p className={styles.description}>
            If this Employee is linked to a Kanjoy User, that User{'’'}s access to this organization
            will also be revoked. History is preserved and nothing is deleted.
          </p>
          {employee.deactivatedAt && (
            <p className={styles.muted}>
              Already deactivated on {formatDayFull(employee.deactivatedAt)}.
            </p>
          )}
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
          <div className={styles.formActions}>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              disabled={deactivate.isPending}
              onClick={() => {
                void handleConfirm();
              }}
            >
              {deactivate.isPending ? 'Deactivating…' : 'Deactivate'}
            </Button>
          </div>
        </div>
      ) : (
        <p className={styles.placeholder}>No employee selected.</p>
      )}
    </SlidePanel>
  );
}
